import PropTypes from "prop-types";
import { Field, ErrorMessage } from "formik";
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";

const FormField = ({
  name,
  label,
  type = "text",
  as,
  options = [],
  className = "",
  placeholder,
  required,
  onChange,
}) => {
  const baseInputClasses = `
    block w-full px-4 py-2.5 bg-white border border-gray-300 rounded-md
    text-gray-900 text-base leading-6 placeholder-gray-400
    transition-all duration-200 ease-in-out
    hover:border-blue-400
    focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none
    disabled:opacity-50 disabled:cursor-not-allowed
    ${className}
  `;

  const errorInputClasses =
    "border-red-500 hover:border-red-500 focus:border-red-500 focus:ring-red-500/20";

  const checkboxClasses = `
    h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500
    ${className}
  `;

  return (
    <div className="mb-5">
      <label
        htmlFor={name}
        className="text-[15px] font-medium text-gray-700 mb-2 flex items-center"
      >
        {label}
        {required && (
          <span className="text-red-500 ml-1 text-base" aria-label="required">
            *
          </span>
        )}
      </label>
      <div className="relative">
        <Field name={name}>
          {({ field, form }) => {
            const isError = form.touched[name] && form.errors[name];

            if (type === "checkbox") {
              // Handle both single checkbox and multi-checkbox scenarios
              if (options.length === 1) {
                // Single checkbox (e.g., consent_to_share)
                const option = options[0];
                return (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id={name}
                      name={name}
                      checked={field.value === true || field.value === "true"}
                      onChange={(e) => {
                        form.setFieldValue(name, e.target.checked);
                        if (onChange) onChange(e);
                      }}
                      className={`${checkboxClasses} ${isError ? "border-red-500" : ""}`}
                      aria-checked={field.value === true || field.value === "true"}
                      aria-describedby={isError ? `${name}-error` : undefined}
                    />
                    <label htmlFor={name} className="ml-2 text-gray-700">
                      {option.name || option.label || "Unnamed Option"}
                    </label>
                    {isError && (
                      <p
                        id={`${name}-error`}
                        className="text-sm font-medium text-red-600 mt-2"
                        role="alert"
                      >
                        {form.errors[name]}
                      </p>
                    )}
                  </div>
                );
              } else {
                // Multiple checkboxes (e.g., system_functions)
                return (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {Array.isArray(options) && options.length > 0 ? (
                      options.map((option) =>
                        option && option.id !== undefined && option.id !== null ? (
                          <div key={option.id} className="flex items-center">
                            <input
                              type="checkbox"
                              id={`${name}_${option.id}`}
                              name={name}
                              value={option.id.toString()}
                              checked={field.value?.includes(option.id.toString())}
                              onChange={(e) => {
                                const { value, checked } = e.target;
                                const currentValues = field.value || [];
                                if (checked) {
                                  form.setFieldValue(name, [...currentValues, value]);
                                } else {
                                  form.setFieldValue(
                                    name,
                                    currentValues.filter((v) => v !== value)
                                  );
                                }
                                if (onChange) onChange(e);
                              }}
                              className={`${checkboxClasses} ${isError ? "border-red-500" : ""}`}
                              aria-checked={field.value?.includes(option.id.toString())}
                              aria-describedby={isError ? `${name}-error` : undefined}
                            />
                            <label
                              htmlFor={`${name}_${option.id}`}
                              className="ml-2 text-gray-700"
                            >
                              {option.name || "Unnamed Option"}
                            </label>
                          </div>
                        ) : null
                      )
                    ) : (
                      <p className="text-gray-500">No options available</p>
                    )}
                    {isError && (
                      <p
                        id={`${name}-error`}
                        className="col-span-full text-sm font-medium text-red-600 mt-2"
                        role="alert"
                      >
                        {form.errors[name]}
                      </p>
                    )}
                  </div>
                );
              }
            }

            if (type === "file") {
              const getFileSizeLimit = () => {
                if (name === 'malware_sample') return { limit: 50 * 1024 * 1024, text: '50MB' };
                if (name === 'additional_files' || name === 'evidence_files' || name === 'supporting_files') return { limit: 10 * 1024 * 1024, text: '10MB' };
                return { limit: 10 * 1024 * 1024, text: '10MB' };
              };
              
              const { limit, text } = getFileSizeLimit();
              
              const validateFileSize = (files) => {
                const oversizedFiles = [];
                Array.from(files).forEach(file => {
                  if (file.size > limit) {
                    oversizedFiles.push(`${file.name} (${(file.size / (1024 * 1024)).toFixed(1)}MB)`);
                  }
                });
                return oversizedFiles;
              };
              
              return (
                <>
                  <input
                    type="file"
                    id={name}
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      const oversizedFiles = validateFileSize(files);
                      
                      if (oversizedFiles.length > 0) {
                        form.setFieldError(name, `Files exceed ${text} limit: ${oversizedFiles.join(', ')}`);
                        e.target.value = ''; // Clear the input
                        return;
                      }
                      
                      form.setFieldValue(name, files);
                      form.setFieldError(name, undefined); // Clear any previous errors
                      if (onChange) onChange(e);
                    }}
                    className={`${baseInputClasses} ${isError ? errorInputClasses : ""}`}
                    aria-describedby={isError ? `${name}-error` : undefined}
                  />
                  <div className="mt-1 text-sm text-gray-500">
                    Maximum file size: {text} each
                    {name === 'malware_sample' && " (ZIP files only)"}
                  </div>
                  {isError && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ExclamationCircleIcon
                        className="h-5 w-5 text-red-500 animate-pulse"
                        aria-hidden="true"
                      />
                    </div>
                  )}
                </>
              );
            }

            return (
              <>
                {as === "select" ? (
                  <select
                    {...field}
                    id={name}
                    onChange={(e) => {
                      field.onChange(e);
                      if (onChange) onChange(e);
                    }}
                    className={`${baseInputClasses} ${isError ? errorInputClasses : ""}`}
                    aria-describedby={isError ? `${name}-error` : undefined}
                  >
                    <option value="" className="text-gray-400">
                      {placeholder || "Select an option"}
                    </option>
                    {Array.isArray(options) && options.length > 0 ? (
                      options.map((option) => {
                        const optionValue =
                          option.id !== undefined && option.id !== null
                            ? option.id.toString()
                            : option.value !== undefined && option.value !== null
                            ? option.value.toString()
                            : "";
                        const optionLabel = option.name || option.label || "Unnamed Option";

                        return optionValue ? (
                          <option
                            key={optionValue}
                            value={optionValue}
                            className="text-gray-900"
                          >
                            {optionLabel}
                          </option>
                        ) : null;
                      })
                    ) : (
                      <option value="" disabled>
                        No options available
                      </option>
                    )}
                  </select>
                ) : as === "textarea" ? (
                  <textarea
                    {...field}
                    id={name}
                    placeholder={placeholder}
                    onChange={(e) => {
                      field.onChange(e);
                      if (onChange) onChange(e);
                    }}
                    className={`${baseInputClasses} ${isError ? errorInputClasses : ""} min-h-[120px] resize-none`}
                    aria-describedby={isError ? `${name}-error` : undefined}
                  />
                ) : (
                  <input
                    {...field}
                    type={type}
                    id={name}
                    placeholder={placeholder}
                    onChange={(e) => {
                      field.onChange(e);
                      if (onChange) onChange(e);
                    }}
                    className={`${baseInputClasses} ${isError ? errorInputClasses : ""}`}
                    aria-describedby={isError ? `${name}-error` : undefined}
                  />
                )}
                {isError && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ExclamationCircleIcon
                      className="h-5 w-5 text-red-500 animate-pulse"
                      aria-hidden="true"
                    />
                  </div>
                )}
              </>
            );
          }}
        </Field>
      </div>
      <ErrorMessage name={name}>
        {(msg) => (
          <p
            id={`${name}-error`}
            className="mt-2 text-sm font-medium text-red-600 transition-all duration-200 ease-in"
            role="alert"
          >
            {msg}
          </p>
        )}
      </ErrorMessage>
    </div>
  );
};

FormField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.string,
  as: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
  className: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  onChange: PropTypes.func,
};

FormField.defaultProps = {
  type: "text",
  options: [],
  className: "",
  required: false,
};

export default FormField;